'use client'

/**
 * Course rating widget — tap a star to submit (or update) your 1–5 rating
 * for the course. Shown to enrolled learners on the course overview page
 * and at the end of the in-course viewer.
 *
 * Single-tap submit (no separate form): a numeric rating has nothing else
 * to fill in, so clicking a star POSTs immediately and confirms. If the
 * user already rated, the stars are pre-filled and re-tapping updates it.
 */
import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { StarRating } from '@/components/ui/star-rating'

type Status = 'idle' | 'saving' | 'saved' | 'error'

export function CourseRatingWidget({
  courseSanityId,
  initialRating,
  heading,
  className,
}: {
  courseSanityId: string
  initialRating: number | null
  heading?: string
  className?: string
}) {
  const t = useTranslations('courseDetail.rating')
  const [rating, setRating] = useState<number>(initialRating ?? 0)
  const [status, setStatus] = useState<Status>('idle')
  const [hasRated, setHasRated] = useState<boolean>(initialRating != null)

  const submit = async (next: number) => {
    const prev = rating
    setRating(next) // optimistic
    setStatus('saving')
    try {
      const res = await fetch('/api/courses/ratings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ courseSanityId, rating: next }),
      })
      if (!res.ok) {
        setRating(prev) // roll back
        setStatus('error')
        return
      }
      setHasRated(true)
      setStatus('saved')
    } catch {
      setRating(prev)
      setStatus('error')
    }
  }

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-sm font-semibold text-ink">
          {hasRated ? t('yourRating') : (heading ?? t('rateThisCourse'))}
        </span>
        <StarRating
          value={rating}
          onChange={submit}
          size="lg"
          aria-label={t('rateThisCourse')}
        />
        <span aria-live="polite" className="text-xs text-muted-foreground">
          {status === 'saving' && t('saving')}
          {status === 'saved' && t('saved')}
          {status === 'error' && <span className="text-destructive">{t('error')}</span>}
        </span>
      </div>
    </div>
  )
}
